import React, { useMemo, useState } from 'react';
import HospitalSelector from '../../shared/HospitalSelector';
import StatRow from './StatRow';
import ThroughputChart from './ThroughputChart';
import HourlyDetail from './HourlyDetail';
import StatusDonut from './StatusDonut';
import CompanyProgress from './CompanyProgress';
import AttentionCard from './AttentionCard';
import InsightCard from './InsightCard';
import ReminderModal from './ReminderModal';
import PendingListModal from './PendingListModal';
import useMonitoringData from './useMonitoringData';
import { fill } from './labels';

function csrfToken() {
  var tag = document.querySelector('meta[name="csrf-token"]');
  return tag ? tag.getAttribute('content') : '';
}

// The date the server considers "today" is read from the payload; the browser clock is
// never used to derive it (PE-440 defect 7).
export default function MonitoringDashboard({
  labels, hospitals, export_url: exportUrl, reminder_url: reminderUrl, locale
}) {
  var [date, setDate] = useState('');
  var [hospitalId, setHospitalId] = useState(null);
  var [reminderOpen, setReminderOpen] = useState(false);
  var [pendingListOpen, setPendingListOpen] = useState(false);
  var [flash, setFlash] = useState(null);

  var state = useMonitoringData(date, hospitalId);
  var data = state.data;
  var effectiveDate = (data && data.date) || date;

  var currentHour = useMemo(function () {
    if (!data || data.date !== data.default_date) return null;
    return new Date().getHours();
  }, [data]);

  var defaultOpenHour = useMemo(function () {
    if (!data || !data.hourly_data.length) return null;
    var reached = data.hourly_data.filter(function (block) {
      return currentHour === null || block.hour <= currentHour;
    });
    var target = reached.length ? reached[reached.length - 1] : data.hourly_data[data.hourly_data.length - 1];
    return target.hour;
  }, [data, currentHour]);

  function exportHref(format) {
    var params = new URLSearchParams({ date: effectiveDate, format: format });
    if (hospitalId) params.set('hospital_id', hospitalId);
    return exportUrl + '?' + params.toString();
  }

  function sendReminders() {
    return fetch(reminderUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify({ date: effectiveDate, hospital_id: hospitalId })
    }).
      then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      }).
      then(function (json) {
        setReminderOpen(false);
        setFlash(
          fill(labels.reminder.success, { count: json.sent }) + ' ' +
          fill(labels.reminder.skipped, { count: json.skipped })
        );
        return state.reload({ background: true });
      });
  }

  var pendingCount = (data && data.attention && data.attention.not_checked_in) || 0;
  var runningCount = (data && data.funnel && data.funnel.in_process) || 0;

  return (
    <div className="ehc-monitoring">
      <header className="ehc-monitoring__header">
        <div>
          <div className="ehc-monitoring__title-row">
            <h1 className="ehc-monitoring__title">{labels.page.title}</h1>
            {!state.refreshPaused && (
              <span className="badge badge-pill ehc-monitoring__live">{labels.page.live}</span>
            )}
          </div>
          <p className="ehc-monitoring__subtitle">
            {fill(labels.page.subtitle, {
              date: effectiveDate
                ? new Date(effectiveDate + 'T00:00:00').toLocaleDateString(locale, {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })
                : '',
              time: state.updatedAt
                ? state.updatedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
                : '—'
            })}
          </p>
        </div>

        <div className="ehc-monitoring__actions">
          <div className="ehc-monitoring__date">
            <input
              type="date"
              className="ehc-monitoring__date-input"
              aria-label={labels.page.date_label}
              value={effectiveDate}
              onChange={function (event) { setDate(event.target.value); }}
            />
          </div>

          <div className="ehc-monitoring__hospital-filter">
            <HospitalSelector
              hospitals={hospitals}
              selectedId={hospitalId}
              onSelect={setHospitalId}
              onClear={function () { setHospitalId(null); }}
              translations={{
                select_hospital: labels.page.all_hospitals,
                search_hospital: labels.page.hospital_filter,
                no_hospitals_found: labels.page.no_hospitals_found
              }}
            />
          </div>

          <div className="dropdown">
            <button
              type="button"
              className="btn btn-outline-secondary dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              {labels.export.label}
            </button>
            <div className="dropdown-menu dropdown-menu-right">
              <a className="dropdown-item" href={exportHref('csv')}>
                <strong>{labels.export.csv}</strong>
                <small className="d-block text-muted">{labels.export.csv_sub}</small>
              </a>
              <a className="dropdown-item" href={exportHref('xlsx')}>
                <strong>{labels.export.xlsx}</strong>
                <small className="d-block text-muted">{labels.export.xlsx_sub}</small>
              </a>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={state.retry}
            disabled={state.loading}
          >
            <i className={'zmdi zmdi-refresh' + (state.loading ? ' zmdi-hc-spin' : '') + ' mr-1'} />
            {labels.page.refresh}
          </button>
        </div>
      </header>

      {flash && <div className="alert alert-success">{flash}</div>}

      {state.refreshPaused && (
        <div className="alert alert-warning">
          {fill(labels.states.refresh_paused, { count: state.maxFailures })}
        </div>
      )}

      {state.error && !data && (
        <div className="alert alert-danger">
          <strong className="d-block">{labels.states.error_title}</strong>
          {fill(labels.states.error_body, {
            message: state.error,
            time: state.updatedAt
              ? state.updatedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
              : '—'
          })}
          <button type="button" className="btn btn-sm btn-outline-danger ml-2" onClick={state.retry}>
            {labels.states.error_retry}
          </button>
        </div>
      )}

      {state.loading && !data && (
        <div className="ehc-monitoring__skeleton" aria-busy="true" aria-label={labels.states.loading}>
          <div className="ehc-monitoring__skeleton-stats" />
          <div className="ehc-monitoring__skeleton-body" />
        </div>
      )}

      {data && (
        <React.Fragment>
          <StatRow
            funnel={data.funnel}
            statsContext={data.stats_context}
            summary={data.summary}
            labels={labels.stats}
          />

          {data.funnel.scheduled === 0 && data.total === 0 ? (
            <div className="card ehc-monitoring__card">
              <div className="card-body text-center py-5">
                <i className="zmdi zmdi-account-o zmdi-hc-3x text-muted" />
                <h5 className="mt-3">{labels.states.empty_title}</h5>
                <p className="text-muted mb-0">
                  {fill(labels.states.empty_body, { date: effectiveDate })}
                </p>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-lg-8">
                <ThroughputChart
                  hourlyData={data.hourly_data}
                  summary={data.summary}
                  currentHour={currentHour}
                  labels={labels.throughput}
                />
                <HourlyDetail
                  hourlyData={data.hourly_data}
                  defaultOpenHour={defaultOpenHour}
                  running={runningCount}
                  labels={labels.hourly}
                />
              </div>

              <div className="col-lg-4">
                <StatusDonut funnel={data.funnel} labels={labels.status_card} />
                <CompanyProgress
                  companies={data.companies}
                  activeCount={data.stats_context && data.stats_context.companies}
                  labels={labels.companies_card}
                />
                <AttentionCard
                  attention={data.attention}
                  onRemind={function () { setReminderOpen(true); }}
                  onViewList={function () { setPendingListOpen(true); }}
                  labels={labels.attention}
                />
                <InsightCard summary={data.summary} labels={labels.insight} />
              </div>
            </div>
          )}
        </React.Fragment>
      )}

      {reminderOpen && (
        <ReminderModal
          pendingCount={pendingCount}
          date={effectiveDate}
          onClose={function () { setReminderOpen(false); }}
          onSubmit={sendReminders}
          labels={labels.reminder}
        />
      )}
      {pendingListOpen && (
        <PendingListModal
          date={effectiveDate}
          hospitalId={hospitalId}
          count={pendingCount}
          onClose={function () { setPendingListOpen(false); }}
          onRemind={function () { setPendingListOpen(false); setReminderOpen(true); }}
          labels={labels.pending_list}
        />
      )}
    </div>
  );
}
